import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { newsApi, type PublicNews } from '@/api/news'
import { NewsArticleModal } from '@/components/news/NewsArticleModal'
import { ROUTES } from '@/utils/constants'
import { formatDateDMY } from '@/utils/date'

export function News() {
  const { t } = useTranslation()
  const [news, setNews] = useState<PublicNews[]>([])
  const [loading, setLoading] = useState(true)
  const [openSlug, setOpenSlug] = useState<string | null>(null)

  useEffect(() => {
    newsApi
      .getPublicList(6)
      .then(setNews)
      .catch(() => setNews([]))
      .finally(() => setLoading(false))
  }, [])

  // Пустой фид — секция скрывается целиком (якорь #news остаётся для меню)
  if (!loading && news.length === 0) {
    return <section id="news" />
  }

  return (
    <section className="news" id="news">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">{t('landing.news.title')}</h2>
          <p className="section-subtitle">{t('landing.news.subtitle')}</p>
        </div>
        <div className="news-grid">
          {loading
            ? [1, 2, 3].map((i) => (
                <div key={i} className="news-card news-card-skeleton" aria-hidden>
                  <div className="news-skeleton-line news-skeleton-title" />
                  <div className="news-skeleton-line" />
                  <div className="news-skeleton-line" />
                  <div className="news-skeleton-line news-skeleton-short" />
                </div>
              ))
            : news.map((item) => (
                <Link
                  key={item.slug}
                  to={ROUTES.NEWS_VIEW(item.slug)}
                  className="news-card news-card-link"
                  onClick={(event) => {
                    // Обычный клик открывает модалку; Ctrl/⌘/средняя кнопка —
                    // штатный переход по ссылке (ссылка настоящая, её можно копировать)
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
                    event.preventDefault()
                    setOpenSlug(item.slug)
                  }}
                >
                  {item.cover_url && (
                    <img className="news-card-cover" src={item.cover_url} alt={item.title} loading="lazy" />
                  )}
                  <h3>{item.title}</h3>
                  <p>{item.preview}</p>
                  {(item.tags?.length ?? 0) > 0 && (
                    <div className="news-card-tags">
                      {item.tags!.map((tag) => (
                        <span key={tag.id} className="news-card-tag" style={{ backgroundColor: tag.color }}>
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="news-date">
                    {item.published_at ? formatDateDMY(item.published_at) : ''}
                  </div>
                </Link>
              ))}
        </div>
        <div className="news-page-more">
          <Link to={ROUTES.NEWS} className="btn-secondary">
            {t('news.allNews')}
          </Link>
        </div>
      </div>

      <NewsArticleModal slug={openSlug} onClose={() => setOpenSlug(null)} />
    </section>
  )
}
